/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ENTRY_DATA_CONTINGENT_PAYMENT_KEYS as CONTINGENT_PAYMENT_SHELL_KEYS,
  ENTRY_DATA_DEAL_TYPE_AND_STATUS as DEAL_TYPE_AND_STATUS,
  ENTRY_DATA_DRUG_PROFILE_GROUP_TABLE_KEY as DRUG_PROFILE,
  ENTRY_DATA_DRUG_PROFILE_SHELL_KEYS as DRUG_PROFILE_SHELL_KEYS,
  ENTRY_DATA_KEY_COMMENTS_GROUP_TABLE_KEY as KEY_COMMENTS,
  ENTRY_DATA_KEY_COMMENTS_SHELL_KEYS as KEY_COMMENTS_SHELL_KEYS,
  ENTRY_DATA_LICENSOR_TABLE_KEYS as LICENSOR_TABLE_SHELL_KEYS,
  ENTRY_DATA_LICENSOR_TABLE as LICENSOR_TABLE,
  ENTRY_DATA_LICENSEE_TABLE as LICENSEE_TABLE,
  ENTRY_DATA_LICENSEE_TABLE_KEYS as LICENSEE_TABLE_GROUP_SHELL_KEYS,
  ENTRY_DATA_RIGHTS_SCOPE_KEYS as RIGHTS_SCOPE_SHELL_KEYS,
  ENTRY_DATA_MERGE_ACQUISITION_TERMS_KEYS as MERGE_ACQUISITION_TERMS_KEYS,
  ENTRY_DATA_MILESTONE_PAYMENT_CONDITION_KEYS as MILESTONE_PAYMENT_CONDITION_SHELL_KEYS,
  ENTRY_DATA_OTHER_PAYMENT_DETAIL_KEYS as OTHER_PAYMENT_DETAIL_SHELL_KEYS,
  ENTRY_DATA_STRATEGIC_COLLABORATION_LICENSE_AGREEMENT_TERMS_KEYS as STRATEGIC_COLLABORATION_LICENSE_AGREEMENT_TERMS_KEYS,
  ENTRY_DATA_ADDITIONAL_INFORMATION_DEAL as ADDITIONAL_INFORMATION,
  ENTRY_DATA_ADDITIONAL_INFORMATION_DEAL_KEYS as ADDITIONAL_INFORMATION_SHELL_KEYS,
  ENTRY_DATA_DEAL_TYPE_AND_STATUS_KEYS as DEAL_TYPE_AND_STATUS_SHELL_KEYS,
  ENTRY_DATA_SECTION_KEYS as SECTION_KEYS,
  ENTRY_DATA_VENTURE_FORMATION_TERMS_KEYS,
} from "@frontend/common";

import { ArticleEntryData } from "@/types/articleEntryData.types";
import {
  DealLaScAdvisorTableValue,
  DealLaScMainFinancialTermsValue,
  DealLaScRightsScopeTableValue,
  DealsLaSc,
} from "@/types/dealLaSc.types";

const getDrugProfileFormatCheckList = (
  entryData: ArticleEntryData,
  sectionKey: typeof SECTION_KEYS.deals | typeof SECTION_KEYS.dealsLaSc,
) => {
  const targetPath = entryData?.[sectionKey]?.[DRUG_PROFILE];
  if (!targetPath) return [`${DRUG_PROFILE} data is missing`];
  if (!Array.isArray(targetPath)) return [`${DRUG_PROFILE} data is missing`];
  if (targetPath.length === 0) return [`${DRUG_PROFILE} data is missing`];

  const checkDrugProfileTableValues = targetPath.flatMap(
    (tableItem, tableIndex) => {
      const requiredFields = [
        {
          key: DRUG_PROFILE_SHELL_KEYS.brandName,
          label: DRUG_PROFILE_SHELL_KEYS.brandName,
        },
        {
          key: DRUG_PROFILE_SHELL_KEYS.innName,
          label: DRUG_PROFILE_SHELL_KEYS.innName,
        },
        {
          key: DRUG_PROFILE_SHELL_KEYS.codeName,
          label: DRUG_PROFILE_SHELL_KEYS.codeName,
        },
        {
          key: DRUG_PROFILE_SHELL_KEYS.moa,
          label: DRUG_PROFILE_SHELL_KEYS.moa,
        },
        {
          key: DRUG_PROFILE_SHELL_KEYS.modality,
          label: DRUG_PROFILE_SHELL_KEYS.modality,
        },
        {
          key: DRUG_PROFILE_SHELL_KEYS.otherCharacteristics,
          label: DRUG_PROFILE_SHELL_KEYS.otherCharacteristics,
        },
        {
          key: DRUG_PROFILE_SHELL_KEYS.roa,
          label: DRUG_PROFILE_SHELL_KEYS.roa,
        },
        {
          key: DRUG_PROFILE_SHELL_KEYS.target,
          label: DRUG_PROFILE_SHELL_KEYS.target,
        },
        {
          key: DRUG_PROFILE_SHELL_KEYS.targetIndication,
          label: DRUG_PROFILE_SHELL_KEYS.targetIndication,
        },
        {
          key: DRUG_PROFILE_SHELL_KEYS.phase,
          label: DRUG_PROFILE_SHELL_KEYS.phase,
        },
        {
          key: DRUG_PROFILE_SHELL_KEYS.originatorDeveloper,
          label: DRUG_PROFILE_SHELL_KEYS.originatorDeveloper,
        },
        {
          key: DRUG_PROFILE_SHELL_KEYS.genericBiosimilar,
          label: DRUG_PROFILE_SHELL_KEYS.genericBiosimilar,
        },
        {
          key: DRUG_PROFILE_SHELL_KEYS.refProductName,
          label: DRUG_PROFILE_SHELL_KEYS.refProductName,
        },
      ];

      return requiredFields
        .map(({ key, label }) =>
          !tableItem[key]?.value
            ? `${label} is missing in ${DRUG_PROFILE} table ${tableIndex}`
            : "",
        )
        .filter((message) => !!message);
    },
  );

  return [...checkDrugProfileTableValues];
};

const getDealTypeAndStatusFormatCheckList = (entryData: ArticleEntryData) => {
  const targetPath = entryData?.[SECTION_KEYS.deals]?.[DEAL_TYPE_AND_STATUS];
  if (!targetPath) return [`${DEAL_TYPE_AND_STATUS} data is missing`];

  const requiredFields = [
    {
      key: DEAL_TYPE_AND_STATUS_SHELL_KEYS.dealStatus,
      label: DEAL_TYPE_AND_STATUS_SHELL_KEYS.dealStatus,
    },
    {
      key: DEAL_TYPE_AND_STATUS_SHELL_KEYS.dealType,
      label: DEAL_TYPE_AND_STATUS_SHELL_KEYS.dealType,
    },
    {
      key: DEAL_TYPE_AND_STATUS_SHELL_KEYS.dealName,
      label: DEAL_TYPE_AND_STATUS_SHELL_KEYS.dealName,
    },
  ];

  return (
    requiredFields
      ?.map(({ key, label }) =>
        !targetPath[key]?.value
          ? `${label} is missing in ${DEAL_TYPE_AND_STATUS}`
          : "",
      )
      ?.filter((message) => !!message) || []
  );
};

const getLicensorTableFormatCheckList = (entryData: ArticleEntryData) => {
  const targetPath = entryData?.[SECTION_KEYS.deals]?.[LICENSOR_TABLE];
  if (!targetPath) return [`${LICENSOR_TABLE} data is missing`];

  const requiredFields = [
    {
      key: LICENSOR_TABLE_SHELL_KEYS.licensorName,
      label: LICENSOR_TABLE_SHELL_KEYS.licensorName,
    },
    {
      key: LICENSOR_TABLE_SHELL_KEYS.licensorResponsibilityRights,
      label: LICENSOR_TABLE_SHELL_KEYS.licensorResponsibilityRights,
    },
  ];

  const singleFieldErrors =
    requiredFields
      ?.map(({ key, label }) =>
        !targetPath[key]?.value
          ? `${label} is missing in ${LICENSOR_TABLE}`
          : "",
      )
      ?.filter((message) => !!message) || [];

  const licensorMAPath = targetPath["Licensor(MA)"];
  const licensorMAErrors =
    licensorMAPath?.length === 0
      ? [`Licensor(MA) is missing in ${LICENSOR_TABLE}`]
      : licensorMAPath?.flatMap((item, index) => {
          const requiredMAFields = [
            {
              key: "Licensor Name(MA)" as const,
              label: "Licensor Name(MA)",
            },
            {
              key: "Licensor Relationship Context(MA)" as const,
              label: "Licensor Relationship Context(MA)",
            },
          ];

          return requiredMAFields
            .map(({ key, label }) =>
              !item[key]?.value
                ? `${label} is missing in ${LICENSOR_TABLE} Licensor(MA) group ${index}`
                : "",
            )
            .filter((message) => !!message);
        }) ?? [];

  return [...singleFieldErrors, ...licensorMAErrors];
};

const getLicenseeTableFormatCheckList = (entryData: ArticleEntryData) => {
  const targetPath = entryData?.[SECTION_KEYS.deals]?.[LICENSEE_TABLE];
  if (!targetPath) return [`${LICENSEE_TABLE} data is missing`];
  if (!Array.isArray(targetPath)) return [`${LICENSEE_TABLE} data is missing`];
  if (targetPath.length === 0) return [`${LICENSEE_TABLE} data is missing`];

  const checkLicenseeTableValues = targetPath.flatMap(
    (tableItem, tableIndex) => {
      const requiredSingleFields = [
        {
          key: LICENSEE_TABLE_GROUP_SHELL_KEYS.licenseeName,
          label: LICENSEE_TABLE_GROUP_SHELL_KEYS.licenseeName,
        },
      ];

      const singleFieldsList = requiredSingleFields
        .map(({ key, label }) =>
          !tableItem[key]
            ? `${label} is missing in ${LICENSEE_TABLE} ${tableIndex}`
            : "",
        )
        .filter((message) => !!message);

      const rightsScope =
        tableItem?.[LICENSEE_TABLE_GROUP_SHELL_KEYS.rightsScope];

      const rightsScopeGroupList =
        rightsScope?.length === 0
          ? [
              `${LICENSEE_TABLE_GROUP_SHELL_KEYS.rightsScope} is missing in ${LICENSEE_TABLE} table ${tableIndex}`,
            ]
          : [];

      const rightsScopeFieldsList = rightsScope.flatMap(
        (groupShellItem, groupIndex) => {
          const requiredFields = [
            {
              key: RIGHTS_SCOPE_SHELL_KEYS.drug,
              label: RIGHTS_SCOPE_SHELL_KEYS.drug,
            },
            {
              key: RIGHTS_SCOPE_SHELL_KEYS.exclusivity,
              label: RIGHTS_SCOPE_SHELL_KEYS.exclusivity,
            },
            {
              key: RIGHTS_SCOPE_SHELL_KEYS.indication,
              label: RIGHTS_SCOPE_SHELL_KEYS.indication,
            },
            {
              key: RIGHTS_SCOPE_SHELL_KEYS.territory,
              label: RIGHTS_SCOPE_SHELL_KEYS.territory,
            },
          ];

          return requiredFields
            .map(({ key, label }) =>
              !groupShellItem[key]?.value
                ? `${label} is missing in ${LICENSEE_TABLE} table ${tableIndex} ${
                    LICENSEE_TABLE_GROUP_SHELL_KEYS.rightsScope
                  } group ${groupIndex + 1}`
                : "",
            )
            .filter((message) => !!message);
        },
      );

      const strategicCollaborationLicenseAgreementTerms =
        tableItem?.[
          LICENSEE_TABLE_GROUP_SHELL_KEYS
            .strategicCollaborationLicenseAgreementTerms
        ];

      const requiredScLaTermsSingleFields = [
        {
          key: STRATEGIC_COLLABORATION_LICENSE_AGREEMENT_TERMS_KEYS.otherFinancialTermsScLa,
          label:
            STRATEGIC_COLLABORATION_LICENSE_AGREEMENT_TERMS_KEYS.otherFinancialTermsScLa,
        },
        {
          key: STRATEGIC_COLLABORATION_LICENSE_AGREEMENT_TERMS_KEYS.licenseeResponsibilityAndRightsScLa,
          label:
            STRATEGIC_COLLABORATION_LICENSE_AGREEMENT_TERMS_KEYS.licenseeResponsibilityAndRightsScLa,
        },
        {
          key: STRATEGIC_COLLABORATION_LICENSE_AGREEMENT_TERMS_KEYS.royalty,
          label: STRATEGIC_COLLABORATION_LICENSE_AGREEMENT_TERMS_KEYS.royalty,
        },
        {
          key: STRATEGIC_COLLABORATION_LICENSE_AGREEMENT_TERMS_KEYS.totalPaymentScLa,
          label:
            STRATEGIC_COLLABORATION_LICENSE_AGREEMENT_TERMS_KEYS.totalPaymentScLa,
        },
        {
          key: STRATEGIC_COLLABORATION_LICENSE_AGREEMENT_TERMS_KEYS.upfrontPayment,
          label:
            STRATEGIC_COLLABORATION_LICENSE_AGREEMENT_TERMS_KEYS.upfrontPayment,
        },
        {
          key: STRATEGIC_COLLABORATION_LICENSE_AGREEMENT_TERMS_KEYS.disclosedPaymentsSumScLa,
          label:
            STRATEGIC_COLLABORATION_LICENSE_AGREEMENT_TERMS_KEYS.disclosedPaymentsSumScLa,
        },
      ];

      const singleFieldsListScLa = requiredScLaTermsSingleFields
        .map(({ key, label }) =>
          !strategicCollaborationLicenseAgreementTerms[key]?.value
            ? `${label} is missing in ${LICENSEE_TABLE} table ${tableIndex}`
            : "",
        )
        .filter((message) => !!message);

      const milestonePaymentAndCondition =
        strategicCollaborationLicenseAgreementTerms?.[
          STRATEGIC_COLLABORATION_LICENSE_AGREEMENT_TERMS_KEYS
            .milestonePaymentAndCondition
        ];

      const milestonePaymentAndConditionGroupList =
        milestonePaymentAndCondition?.length === 0
          ? [
              `${STRATEGIC_COLLABORATION_LICENSE_AGREEMENT_TERMS_KEYS.milestonePaymentAndCondition} is missing in ${LICENSEE_TABLE} table ${tableIndex}`,
            ]
          : [];

      const milestonePaymentAndConditionFieldsList =
        milestonePaymentAndCondition?.flatMap((groupShellItem, groupIndex) => {
          const requiredFields = [
            {
              key: MILESTONE_PAYMENT_CONDITION_SHELL_KEYS.milestoneConditionTitle,
              label:
                MILESTONE_PAYMENT_CONDITION_SHELL_KEYS.milestoneConditionTitle,
            },
            {
              key: MILESTONE_PAYMENT_CONDITION_SHELL_KEYS.milestonePayment,
              label: MILESTONE_PAYMENT_CONDITION_SHELL_KEYS.milestonePayment,
            },
          ];

          return requiredFields
            .map(({ key, label }) =>
              !groupShellItem[key]?.value
                ? `${label} is missing in ${LICENSEE_TABLE} table ${tableIndex} ${STRATEGIC_COLLABORATION_LICENSE_AGREEMENT_TERMS_KEYS.milestonePaymentAndCondition} group ${groupIndex}`
                : "",
            )
            .filter((message) => !!message);
        });

      const otherPaymentAndDetailScLa =
        strategicCollaborationLicenseAgreementTerms?.[
          STRATEGIC_COLLABORATION_LICENSE_AGREEMENT_TERMS_KEYS
            .otherPaymentAndDetailScLa
        ];

      const otherPaymentAndDetailGroupList =
        otherPaymentAndDetailScLa?.length === 0
          ? [
              `${STRATEGIC_COLLABORATION_LICENSE_AGREEMENT_TERMS_KEYS.otherPaymentAndDetailScLa} is missing in ${LICENSEE_TABLE} table ${tableIndex}`,
            ]
          : [];

      const otherPaymentAndDetailFieldsList =
        otherPaymentAndDetailScLa?.flatMap((groupShellItem, groupIndex) => {
          const requiredFields = [
            {
              key: OTHER_PAYMENT_DETAIL_SHELL_KEYS.otherPaymentDetailScLa,
              label: OTHER_PAYMENT_DETAIL_SHELL_KEYS.otherPaymentDetailScLa,
            },
            {
              key: OTHER_PAYMENT_DETAIL_SHELL_KEYS.otherPaymentScLa,
              label: OTHER_PAYMENT_DETAIL_SHELL_KEYS.otherPaymentScLa,
            },
          ];

          return requiredFields
            .map(({ key, label }) =>
              !groupShellItem[key]?.value
                ? `${label} is missing in ${LICENSEE_TABLE} table ${tableIndex} ${STRATEGIC_COLLABORATION_LICENSE_AGREEMENT_TERMS_KEYS.otherPaymentAndDetailScLa} group ${groupIndex}`
                : "",
            )
            .filter((message) => !!message);
        });

      const requiredSingleMergeAcquisitionTermsFields = [
        {
          key: MERGE_ACQUISITION_TERMS_KEYS.licenseResponsibilityRightsMa,
          label: MERGE_ACQUISITION_TERMS_KEYS.licenseResponsibilityRightsMa,
        },
        {
          key: MERGE_ACQUISITION_TERMS_KEYS.totalPaymentMa,
          label: MERGE_ACQUISITION_TERMS_KEYS.totalPaymentMa,
        },
        {
          key: MERGE_ACQUISITION_TERMS_KEYS.upfrontPaymentMa,
          label: MERGE_ACQUISITION_TERMS_KEYS.upfrontPaymentMa,
        },
        {
          key: MERGE_ACQUISITION_TERMS_KEYS.upfrontPricePerShareMa,
          label: MERGE_ACQUISITION_TERMS_KEYS.upfrontPricePerShareMa,
        },
        {
          key: MERGE_ACQUISITION_TERMS_KEYS.mergerFinancialTermsMa,
          label: MERGE_ACQUISITION_TERMS_KEYS.mergerFinancialTermsMa,
        },
        {
          key: MERGE_ACQUISITION_TERMS_KEYS.pricePerShare,
          label: MERGE_ACQUISITION_TERMS_KEYS.pricePerShare,
        },
        {
          key: MERGE_ACQUISITION_TERMS_KEYS.numberOfAcquiringShares,
          label: MERGE_ACQUISITION_TERMS_KEYS.numberOfAcquiringShares,
        },
        {
          key: MERGE_ACQUISITION_TERMS_KEYS.otherFinancialTermsMa,
          label: MERGE_ACQUISITION_TERMS_KEYS.otherFinancialTermsMa,
        },
        {
          key: MERGE_ACQUISITION_TERMS_KEYS.disclosedPaymentsSumMa,
          label: MERGE_ACQUISITION_TERMS_KEYS.disclosedPaymentsSumMa,
        },
        {
          key: MERGE_ACQUISITION_TERMS_KEYS.disclosedPaymentsSumPricePerShareMa,
          label:
            MERGE_ACQUISITION_TERMS_KEYS.disclosedPaymentsSumPricePerShareMa,
        },
      ];

      const singleFieldsListMa = requiredSingleMergeAcquisitionTermsFields
        .map(({ key, label }) =>
          !tableItem[LICENSEE_TABLE_GROUP_SHELL_KEYS.mergeAcquisitionTerms][key]
            ?.value
            ? `${label} is missing in ${LICENSEE_TABLE} table ${tableIndex}`
            : "",
        )
        .filter((message) => !!message);

      const contingentPayment =
        tableItem?.[LICENSEE_TABLE_GROUP_SHELL_KEYS.mergeAcquisitionTerms]?.[
          MERGE_ACQUISITION_TERMS_KEYS.contingentPayment
        ];
      const contingentPaymentGroupList =
        contingentPayment?.length === 0
          ? [
              `${MERGE_ACQUISITION_TERMS_KEYS.contingentPayment} is missing in ${LICENSEE_TABLE} table ${tableIndex}`,
            ]
          : [];

      const checkContingentPaymentFields = contingentPayment?.flatMap(
        (subGroupShellItem, subGroupIndex) => {
          const requiredFields = [
            {
              key: CONTINGENT_PAYMENT_SHELL_KEYS.contingentMilestoneMa,
              label: CONTINGENT_PAYMENT_SHELL_KEYS.contingentMilestoneMa,
            },
            {
              key: CONTINGENT_PAYMENT_SHELL_KEYS.contingentPaymentMa,
              label: CONTINGENT_PAYMENT_SHELL_KEYS.contingentPaymentMa,
            },
            {
              key: CONTINGENT_PAYMENT_SHELL_KEYS.contingentPricePerShareMa,
              label: CONTINGENT_PAYMENT_SHELL_KEYS.contingentPricePerShareMa,
            },
          ];

          return requiredFields
            .map(({ key, label }) =>
              !subGroupShellItem[key]?.value
                ? `${label} is missing in ${LICENSEE_TABLE} table ${tableIndex} ${MERGE_ACQUISITION_TERMS_KEYS.contingentPayment} group ${subGroupIndex}`
                : "",
            )
            .filter((message) => !!message);
        },
      );

      const licenseeMAPath = tableItem?.["Licensee(MA)"];
      const licenseeMAErrors =
        licenseeMAPath?.length === 0
          ? [`Licensee(MA) is missing in ${LICENSEE_TABLE} table ${tableIndex}`]
          : licenseeMAPath?.flatMap((item, groupIndex) => {
              const requiredMAFields = [
                {
                  key: "Licensee Name(MA)" as const,
                  label: "Licensee Name(MA)",
                },
                {
                  key: "Licensee Relationship Context(MA)" as const,
                  label: "Licensee Relationship Context(MA)",
                },
              ];

              return requiredMAFields
                .map(({ key, label }) =>
                  !item[key]?.value
                    ? `${label} is missing in ${LICENSEE_TABLE} table ${tableIndex} Licensee(MA) group ${groupIndex}`
                    : "",
                )
                .filter((message) => !!message);
            }) ?? [];

      return [
        ...(singleFieldsList || []),
        ...(rightsScopeGroupList || []),
        ...(rightsScopeFieldsList || []),
        ...(singleFieldsListScLa || []),
        ...(milestonePaymentAndConditionGroupList || []),
        ...(milestonePaymentAndConditionFieldsList || []),
        ...(otherPaymentAndDetailGroupList || []),
        ...(otherPaymentAndDetailFieldsList || []),
        ...(singleFieldsListMa || []),
        ...(contingentPaymentGroupList || []),
        ...(checkContingentPaymentFields || []),
        ...(licenseeMAErrors || []),
      ];
    },
  );

  return [...checkLicenseeTableValues];
};

const getVentureFormationTermsFormatCheckList = (
  entryData: ArticleEntryData,
) => {
  const targetPath = entryData?.[SECTION_KEYS.deals]?.[LICENSEE_TABLE];
  if (!targetPath) return [`${LICENSEE_TABLE} data is missing`];
  if (!Array.isArray(targetPath)) return [`${LICENSEE_TABLE} data is missing`];
  if (targetPath.length === 0) return [`${LICENSEE_TABLE} data is missing`];

  const checkVentureFormationTerms = targetPath.flatMap(
    (tableItem, tableIndex) => {
      const ventureFormationTerms =
        tableItem?.[LICENSEE_TABLE_GROUP_SHELL_KEYS.ventureFormationTerms];

      const requiredSingleFields = [
        {
          key: ENTRY_DATA_VENTURE_FORMATION_TERMS_KEYS.licenseeResponsibilityAndRightsVf,
          label: "Licensee Responsibility & Rights (VF)",
        },
        {
          key: ENTRY_DATA_VENTURE_FORMATION_TERMS_KEYS.ownershipStructureDetails,
          label: "Ownership Structure Details",
        },
        {
          key: ENTRY_DATA_VENTURE_FORMATION_TERMS_KEYS.totalInvestmentVf,
          label: "Total Investment (VF)",
        },
        {
          key: ENTRY_DATA_VENTURE_FORMATION_TERMS_KEYS.disclosedPaymentsSumVf,
          label: "Disclosed Payments Sum (VF)",
        },
        {
          key: ENTRY_DATA_VENTURE_FORMATION_TERMS_KEYS.initialInvestment,
          label: "Initial Investment",
        },
        {
          key: ENTRY_DATA_VENTURE_FORMATION_TERMS_KEYS.otherFinancialTermsVf,
          label: "Other Financial Terms (VF)",
        },
      ];

      const singleFieldsList = requiredSingleFields
        .map(({ key, label }) =>
          !ventureFormationTerms?.[key]?.value
            ? `${label} is missing in ${LICENSEE_TABLE} table ${tableIndex}`
            : "",
        )
        .filter((message) => !!message);

      const contingentInvestmentAndDetail =
        ventureFormationTerms?.[
          ENTRY_DATA_VENTURE_FORMATION_TERMS_KEYS
            .contingentInvestmentAndDetailVf
        ];

      const contingentInvestmentAndDetailList =
        contingentInvestmentAndDetail?.flatMap((groupShellItem, groupIndex) => {
          const requiredFields = [
            {
              key: ENTRY_DATA_VENTURE_FORMATION_TERMS_KEYS.contingentInvestmentVf,
              label: "Contingent Investment (VF)",
            },
            {
              key: ENTRY_DATA_VENTURE_FORMATION_TERMS_KEYS.contingentDetailVf,
              label: "Contingent Detail (VF)",
            },
          ];

          return requiredFields
            .map(({ key, label }) =>
              !groupShellItem[key]?.value
                ? `${label} is missing in ${LICENSEE_TABLE} table ${tableIndex} Contingent Investment and Detail group ${groupIndex}`
                : "",
            )
            .filter((message) => !!message);
        });

      return [
        ...(singleFieldsList || []),
        ...(contingentInvestmentAndDetailList || []),
      ];
    },
  );

  return [...checkVentureFormationTerms];
};

const getAdditionalInformationFormatCheckList = (
  entryData: ArticleEntryData,
) => {
  const targetPath = entryData?.[SECTION_KEYS.deals]?.[ADDITIONAL_INFORMATION];
  if (!targetPath) return [`${ADDITIONAL_INFORMATION} data is missing`];

  const requiredFields = [
    {
      key: ADDITIONAL_INFORMATION_SHELL_KEYS.additionalInformation,
      label: ADDITIONAL_INFORMATION_SHELL_KEYS.additionalInformation,
    },
    {
      key: ADDITIONAL_INFORMATION_SHELL_KEYS.purposeOfTheDeal,
      label: ADDITIONAL_INFORMATION_SHELL_KEYS.purposeOfTheDeal,
    },
  ];

  return (
    requiredFields
      .map(({ key, label }) =>
        !targetPath[key]?.value
          ? `${label} is missing in ${ADDITIONAL_INFORMATION}`
          : "",
      )
      .filter((message) => !!message) || []
  );
};

const getDealLaScSingleTableFormatCheckList = (entryData: ArticleEntryData) => {
  const targetPath = entryData?.[SECTION_KEYS.dealsLaSc];
  const additionalInformationKey = "Additional Information";
  const dealPurposeKey = "Deal Purpose";
  const otherFinancialTermsKey = "Other Financial Terms";

  if (!targetPath) return [`Deal LA&SC data is missing`];

  const requiredFields = [
    {
      key: additionalInformationKey,
      label: additionalInformationKey,
    },
    {
      key: dealPurposeKey,
      label: dealPurposeKey,
    },
    {
      key: otherFinancialTermsKey,
      label: otherFinancialTermsKey,
    },
  ] as const satisfies ReadonlyArray<{ key: keyof DealsLaSc; label: string }>;

  return (
    requiredFields
      .map(({ key, label }) =>
        !targetPath[key]?.value
          ? `${label} is missing in Deal LA&SC Section`
          : "",
      )
      .filter((message) => !!message) || []
  );
};

const getDealLaScPartnerAxisTableFormatCheckList = (
  entryData: ArticleEntryData,
) => {
  const targetPath = entryData?.[SECTION_KEYS.dealsLaSc]?.["Partner Axis"];

  if (!targetPath) return [`Partner Axis data is missing`];

  const requiredFields = [
    {
      key: "Partner A Responsibility & Rights",
      label: "Partner A Responsibility & Rights",
    },
    {
      key: "Partner B Responsibility & Rights",
      label: "Partner B Responsibility & Rights",
    },
    {
      key: "Partner C Responsibility & Rights",
      label: "Partner C Responsibility & Rights",
    },
  ] as const satisfies ReadonlyArray<{
    key: keyof NonNullable<typeof targetPath>;
    label: string;
  }>;

  const partnerATargetPath = targetPath["Partner A"];
  const partnerBTargetPath = targetPath["Partner B"];
  const partnerCTargetPath = targetPath["Partner C"];

  const partnerARequiredFields = [
    {
      key: "Partner A Name",
      label: "Partner A Name",
    },
    {
      key: "Partner A Relationship Context",
      label: "Partner A Relationship Context",
    },
  ] as const satisfies ReadonlyArray<{
    key: keyof NonNullable<typeof partnerATargetPath>[number];
    label: string;
  }>;

  const partnerBRequiredFields = [
    {
      key: "Partner B Name",
      label: "Partner B Name",
    },
    {
      key: "Partner B Relationship Context",
      label: "Partner B Relationship Context",
    },
  ] as const satisfies ReadonlyArray<{
    key: keyof NonNullable<typeof partnerBTargetPath>[number];
    label: string;
  }>;

  const partnerCRequiredFields = [
    {
      key: "Partner C Name",
      label: "Partner C Name",
    },
    {
      key: "Partner C Relationship Context",
      label: "Partner C Relationship Context",
    },
  ] as const satisfies ReadonlyArray<{
    key: keyof NonNullable<typeof partnerCTargetPath>[number];
    label: string;
  }>;

  return [
    ...(requiredFields
      .map(({ key, label }) =>
        !targetPath[key] ? `${label} is missing in Deal LA&SC Section` : "",
      )
      .filter((message) => !!message) || []),
    ...(partnerATargetPath
      ? partnerARequiredFields
          .map(({ key, label }) =>
            !partnerATargetPath.some((item) => item?.[key]) // Check if any item in the array has the required key
              ? `${label} is missing in Partner A group in Partner Axis table`
              : "",
          )
          .filter((message) => !!message) || []
      : [`Partner A data is missing in Partner Axis table`]),
    ...(partnerBTargetPath
      ? partnerBRequiredFields
          .map(({ key, label }) =>
            !partnerBTargetPath.some((item) => item?.[key]) // Check if any item in the array has the required key
              ? `${label} is missing in Partner B group in Partner Axis table`
              : "",
          )
          .filter((message) => !!message) || []
      : [`Partner B data is missing in Partner Axis table`]),
    ...(partnerCTargetPath
      ? partnerCRequiredFields
          .map(({ key, label }) =>
            !partnerCTargetPath.some((item) => item?.[key]) // Check if any item in the array has the required key
              ? `${label} is missing in Partner C group in Partner Axis table`
              : "",
          )
          .filter((message) => !!message) || []
      : [`Partner C data is missing in Partner Axis table`]),
  ];
};

const getDealLaScRightsScopeFormatCheckList = (entryData: ArticleEntryData) => {
  const targetPath =
    entryData?.[SECTION_KEYS.dealsLaSc]?.["Rights Scope Table"];
  if (!targetPath) return [`Rights Scope Table data is missing`];
  if (!Array.isArray(targetPath)) return [`Rights Scope Table data is missing`];
  if (targetPath.length === 0) return [`Rights Scope Table data is missing`];

  const checkRightsScope = targetPath.flatMap((tableItem, tableIndex) => {
    const rightsScopeData = tableItem;

    const requiredSingleFields = [
      {
        key: "Asset",
        label: "Asset",
      },
      {
        key: "Exclusivity",
        label: "Exclusivity",
      },
      {
        key: "Intended Use",
        label: "Intended Use",
      },
      {
        key: "Rights Limitation",
        label: "Rights Limitation",
      },
      {
        key: "Rights Scope",
        label: "Rights Scope",
      },
      {
        key: "Rights Scope Type",
        label: "Rights Scope Type",
      },
      {
        key: "Territory",
        label: "Territory",
      },
      {
        key: "Territory Include",
        label: "Territory Include",
      },
      {
        key: "Territory Exclude",
        label: "Territory Exclude",
      },
    ] as const satisfies ReadonlyArray<{
      key: Exclude<keyof DealLaScRightsScopeTableValue, "Rights Flow">;
      label: string;
    }>;

    const singleFieldsList = requiredSingleFields
      .map(({ key, label }) =>
        !rightsScopeData?.[key]?.value
          ? `${label} is missing in Rights Scope Table ${tableIndex}`
          : "",
      )
      .filter((message) => !!message);

    const rightsFlow = rightsScopeData?.["Rights Flow"];

    const requiredFields = [
      {
        key: "Axis Direction",
        label: "Axis Direction",
      },
      {
        key: "Benefiting Axis",
        label: "Benefiting Axis",
      },
      {
        key: "Contributing Axis",
        label: "Contributing Axis",
      },
    ] as const satisfies ReadonlyArray<{
      key: keyof NonNullable<DealLaScRightsScopeTableValue["Rights Flow"]>;
      label: string;
    }>;

    const rightsFlowDataList = requiredFields
      .map(({ key, label }) =>
        !rightsFlow?.[key]?.value
          ? `${label} is missing in rights scope table ${tableIndex} rights flow group`
          : "",
      )
      .filter((message) => !!message);

    return [...(singleFieldsList || []), ...(rightsFlowDataList || [])];
  });

  return [...checkRightsScope];
};

const getDealLaScAdvisorFormatCheckList = (entryData: ArticleEntryData) => {
  const targetPath = entryData?.[SECTION_KEYS.dealsLaSc]?.["Advisor Table"];
  if (!targetPath) return [`Advisor Table data is missing`];
  if (!Array.isArray(targetPath)) return [`Advisor Table data is missing`];
  if (targetPath.length === 0) return [`Advisor Table data is missing`];

  const checkAdvisorTable = targetPath.flatMap((tableItem, tableIndex) => {
    const advisorData = tableItem;

    const requiredSingleFields = [
      {
        key: "Advisor Name",
        label: "Advisor Name",
      },
      {
        key: "Advisor Role",
        label: "Advisor Role",
      },
      {
        key: "Party Axis",
        label: "Party Axis",
      },
    ] as const satisfies ReadonlyArray<{
      key: keyof DealLaScAdvisorTableValue;
      label: string;
    }>;

    const singleFieldsList = requiredSingleFields
      .map(({ key, label }) =>
        !advisorData?.[key]?.value
          ? `${label} is missing in Advisor Table ${tableIndex}`
          : "",
      )
      .filter((message) => !!message);

    return [...(singleFieldsList || [])];
  });

  return [...checkAdvisorTable];
};

const getDealLaScMainFinancialTermsFormatCheckList = (
  entryData: ArticleEntryData,
) => {
  const targetPath =
    entryData?.[SECTION_KEYS.dealsLaSc]?.["Main Financial Terms Table"];
  if (!targetPath) return [`Main Financial Terms Table data is missing`];
  if (!Array.isArray(targetPath))
    return [`Main Financial Terms Table data is missing`];
  if (targetPath.length === 0)
    return [`Main Financial Terms Table data is missing`];

  const checkMainFinancialTerms = targetPath.flatMap(
    (tableItem, tableIndex) => {
      const requiredSingleFields = [
        {
          key: "Paying Partner Axis(LA&SC)",
          label: "Paying Partner Axis(LA&SC)",
        },
        {
          key: "Receiving Partner Axis(LA&SC)",
          label: "Receiving Partner Axis(LA&SC)",
        },
        {
          key: "Upfront Payment(LA&SC)",
          label: "Upfront Payment(LA&SC)",
        },
        {
          key: "Total Payment(LA&SC)",
          label: "Total Payment(LA&SC)",
        },
        {
          key: "Disclosed Payment Sum(LA&SC)",
          label: "Disclosed Payment Sum(LA&SC)",
        },
      ] as const satisfies ReadonlyArray<{
        key: Exclude<
          keyof DealLaScMainFinancialTermsValue,
          "Milestone Payment And Detail" | "Other Payment And Detail"
        >;
        label: string;
      }>;

      const singleFieldsList = requiredSingleFields
        .map(({ key, label }) =>
          !tableItem?.[key]?.value
            ? `${label} is missing in Main Financial Terms table ${tableIndex}`
            : "",
        )
        .filter((message) => !!message);

      const milestonePaymentAndDetail =
        tableItem?.["Milestone Payment And Detail"];

      const milestonePaymentAndDetailList = milestonePaymentAndDetail?.flatMap(
        (groupShellItem, groupIndex) => {
          const requiredFields = [
            {
              key: "Milestone Payment(LA&SC)",
              label: "Milestone Payment(LA&SC)",
            },
            {
              key: "Milestone Payment Detail(LA&SC)",
              label: "Milestone Payment Detail(LA&SC)",
            },
          ] as const satisfies ReadonlyArray<{
            key: keyof typeof groupShellItem;
            label: string;
          }>;

          return requiredFields
            .map(({ key, label }) =>
              !groupShellItem[key]?.value
                ? `${label} is missing in Main Financial Terms table ${tableIndex} Milestone Payment and Detail group ${groupIndex}`
                : "",
            )
            .filter((message) => !!message);
        },
      );

      const royalty = tableItem?.["Royalty(LA&SC)"];

      const royaltyList = royalty?.flatMap((groupShellItem, groupIndex) => {
        const requiredFields = [
          {
            key: "Royalty Detail",
            label: "Royalty Detail",
          },
          {
            key: "Royalty Rate",
            label: "Royalty Rate",
          },
        ] as const satisfies ReadonlyArray<{
          key: keyof typeof groupShellItem;
          label: string;
        }>;

        return requiredFields
          .map(({ key, label }) =>
            !groupShellItem[key]?.value
              ? `${label} is missing in Main Financial Terms table ${tableIndex} Royalty group ${groupIndex}`
              : "",
          )
          .filter((message) => !!message);
      });

      const otherPaymentAndDetail = tableItem?.["Other Payment And Detail"];

      const otherPaymentAndDetailList = otherPaymentAndDetail?.flatMap(
        (groupShellItem, groupIndex) => {
          const requiredFields = [
            {
              key: "Other Payment(LA&SC)",
              label: "Other Payment(LA&SC)",
            },
            {
              key: "Other Payment Detail(LA&SC)",
              label: "Other Payment Detail(LA&SC)",
            },
          ] as const satisfies ReadonlyArray<{
            key: keyof typeof groupShellItem;
            label: string;
          }>;

          return requiredFields
            .map(({ key, label }) =>
              !groupShellItem[key]?.value
                ? `${label} is missing in Main Financial Terms table ${tableIndex} Other Payment and Detail group ${groupIndex}`
                : "",
            )
            .filter((message) => !!message);
        },
      );

      return [
        ...(singleFieldsList || []),
        ...(royaltyList || []),
        ...(milestonePaymentAndDetailList || []),
        ...(otherPaymentAndDetailList || []),
      ];
    },
  );

  return [...checkMainFinancialTerms];
};

const getKeyCommentFormatCheckList = (
  entryData: ArticleEntryData,
  sectionKey: typeof SECTION_KEYS.deals | typeof SECTION_KEYS.dealsLaSc,
) => {
  const targetPath = entryData?.[sectionKey]?.[KEY_COMMENTS];
  if (!targetPath) return [`${KEY_COMMENTS} data is missing`];

  const requiredFields = [
    {
      key: KEY_COMMENTS_SHELL_KEYS.quote,
      label: KEY_COMMENTS_SHELL_KEYS.quote,
    },
    {
      key: KEY_COMMENTS_SHELL_KEYS.spokesperson,
      label: KEY_COMMENTS_SHELL_KEYS.spokesperson,
    },
  ];

  return (
    requiredFields
      ?.map(({ key, label }) =>
        !targetPath[key]?.value ? `${label} is missing in ${KEY_COMMENTS}` : "",
      )
      ?.filter((message) => !!message) || []
  );
};

export const getDealEntryDataFormatCheckList = (
  entryData: ArticleEntryData,
) => {
  if (entryData?.[SECTION_KEYS.dealsLaSc] !== undefined) {
    // NOTE: Deal LA&SC 섹션이 존재할 경우, Deal 섹션과 Deal LA&SC 섹션 모두 확인
    return [
      ...getDrugProfileFormatCheckList(entryData, SECTION_KEYS.deals),
      ...getDealTypeAndStatusFormatCheckList(entryData),
      ...getLicensorTableFormatCheckList(entryData),
      ...getLicenseeTableFormatCheckList(entryData),
      ...getVentureFormationTermsFormatCheckList(entryData),
      ...getAdditionalInformationFormatCheckList(entryData),
      ...getDealLaScSingleTableFormatCheckList(entryData),
      ...getDealLaScPartnerAxisTableFormatCheckList(entryData),
      ...getDealLaScRightsScopeFormatCheckList(entryData),
      ...getDealLaScMainFinancialTermsFormatCheckList(entryData),
      ...getDealLaScAdvisorFormatCheckList(entryData),
      ...getKeyCommentFormatCheckList(entryData, SECTION_KEYS.deals),
      ...getKeyCommentFormatCheckList(entryData, SECTION_KEYS.dealsLaSc),
    ];
  }
  // NOTE: Deal LA&SC 섹션이 존재하지 않을 경우, Deal 섹션만 확인
  return [
    ...getDrugProfileFormatCheckList(entryData, SECTION_KEYS.deals),
    ...getDealTypeAndStatusFormatCheckList(entryData),
    ...getLicensorTableFormatCheckList(entryData),
    ...getLicenseeTableFormatCheckList(entryData),
    ...getVentureFormationTermsFormatCheckList(entryData),
    ...getAdditionalInformationFormatCheckList(entryData),
    ...getKeyCommentFormatCheckList(entryData, SECTION_KEYS.deals),
  ];
};
